import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* =======================
   Testimonials Data
======================= */
const testimonials = [
  {
    id: 1,
    name: "Sarah Ahmed",
    role: "CEO, TechNova",
    image: "https://i.pravatar.cc/150?img=32",
    text: "MBA Solutions transformed our workflow. The ChatGPT integration is seamless and the support team is top-notch.",
    rating: 5
  },
  {
    id: 2,
    name: "Ali Khan",
    role: "Marketing Director",
    image: "https://i.pravatar.cc/150?img=12",
    text: "The variety of templates available is insane. It saved us hours of content creation work.",
    rating: 5
  },
  {
    id: 3,
    name: "Fatima Sheikh",
    role: "Freelancer",
    image: "https://i.pravatar.cc/150?img=5",
    text: "Easy to use, affordable, and very effective. I love the custom settings feature.",
    rating: 5
  },
  {
    id: 4,
    name: "Usman Zafar",
    role: "Product Manager",
    image: "https://i.pravatar.cc/150?img=11",
    text: "The free trial was enough to convince me. Now I use it daily for reports and emails.",
    rating: 5
  },
  {
    id: 5,
    name: "Ayesha Malik",
    role: "Content Creator",
    image: "https://i.pravatar.cc/150?img=9",
    text: "Best investment for our team. The interface is clean and AI output is accurate.",
    rating: 5
  }
];

/* =======================
   Component
======================= */
const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [cardsToShow, setCardsToShow] = useState(3);

  /* ---------- Responsive cards count ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setCardsToShow(4);
      else if (window.innerWidth >= 768) setCardsToShow(2);
      else setCardsToShow(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- Auto slide ---------- */
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % testimonials.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isPaused]);

  /* ---------- Visible cards ---------- */
  const visibleTestimonials = [];
  for (let i = 0; i < cardsToShow; i++) {
    visibleTestimonials.push(
      testimonials[(index + i) % testimonials.length]
    );
  }

  /* ---------- Stars ---------- */
  const renderStars = (rating) =>
    Array(rating)
      .fill(0)
      .map((_, i) => (
        <svg
          key={i}
          className="w-5 h-5 text-[var(--bg-primary)] fill-current"
          viewBox="0 0 20 20"
        >
          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      ));

  return (
    <section className="px-4 pt-10 lg:pt-14 bg-gray-50 overflow-hidden">
      <div className="max-w-[94vw] mx-auto px-4 sm:px-6 lg:px-8">

        {/* ===== Heading ===== */}
    <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="text-center mb-8 lg:mb-16"
            >
              <h2 className="text-center text-3xl md:text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-[var(--bg-primary)] drop-shadow-sm mb-2 lg:mb-2">
                 What Our Clients Say
              </h2>
              <div className="w-40 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-2"></div>
              <p className="text-sm md:text-sm text-gray-500 text-center max-w-md mx-auto">
                  Hear from our satisfied clients who trust us for quality, reliability, and excellent service.
              </p>
            </motion.div>

        {/* ===== Slider ===== */}
        <div
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <motion.div
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={(e, info) => {
              if (info.offset.x < -100) {
                setIndex((prev) => (prev + 1) % testimonials.length);
              } else if (info.offset.x > 100) {
                setIndex(
                  (prev) =>
                    (prev - 1 + testimonials.length) % testimonials.length
                );
              }
            }}
            className="cursor-grab active:cursor-grabbing"
          >
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              <AnimatePresence mode="wait">
                {visibleTestimonials.map((testimonial) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, y: 40, scale: 0.92 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -40, scale: 0.92 }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                    whileHover={{
                      y: -8,
                      scale: 1.03,
                      boxShadow: "0px 25px 50px rgba(59,130,246,0.25)"
                    }}
                    className="bg-white rounded-2xl shadow-lg p-6 lg:p-7 border border-gray-100 flex flex-col items-center text-center relative group"
                  >
                    {/* Glow */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition duration-300 rounded-2xl" />

                    {/* Avatar */}
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="relative z-10 w-20 h-20 rounded-full border-4 border-[var(--bg-primary)] mb-5"
                    />

                    {/* Stars */}
                    <div className="relative z-10 flex mb-4">
                      {renderStars(testimonial.rating)}
                    </div>

                    {/* Text */}
                    <p className="relative z-10 text-gray-600 italic mb-6 leading-relaxed">
                      “{testimonial.text}”
                    </p>

                    {/* Name */}
                    <div className="relative z-10 mt-auto">
                      <h4 className="font-bold text-gray-900">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-[var(--bg-primary)] font-medium">
                        {testimonial.role}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* ===== Dots ===== */}
          <div className="flex justify-center mt-10 space-x-2">
            {testimonials.map((_, i) => (
              <span
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${i === index
                    ? "w-8 bg-[var(--bg-primary)]"
                    : "w-2 bg-gray-300"
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
